/**
 * PhotoPicker — single "Upload Photo" action for guest photo attachments.
 * Phase 4C-2: photos are compressed client-side before upload
 * (EXIF-corrected, max long edge 1920px, quality ~0.8, target ≤1.4MB).
 * Guests pick an existing image; there is no in-app camera capture.
 */
import { useEffect, useRef, useState } from "react";
import imageCompression from "browser-image-compression";
import { ImagePlus, RefreshCw, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { T } from "../lib/i18n";

const COMPRESS_OPTS = {
  maxSizeMB: 1.4,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  initialQuality: 0.8,
  preserveExif: false,
};
const SKIP_BELOW_BYTES = 300 * 1024;
const BACKEND_LIMIT_BYTES = 10 * 1024 * 1024;

function fmtSize(bytes) {
  if (bytes == null) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function PhotoPicker({ file, onSelect, onBusy, testIdPrefix = "photo-picker" }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const jobRef = useRef(0);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const setBusy = (v) => {
    setCompressing(v);
    onBusy?.(v);
  };

  const handleChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error(T("Please choose an image file"));
      return;
    }
    // GIFs and already-small files skip re-encoding (never upscale / degrade)
    if (f.type === "image/gif" || f.size < SKIP_BELOW_BYTES) {
      onSelect(f);
      return;
    }
    const job = ++jobRef.current;
    setBusy(true);
    try {
      const compressed = await imageCompression(f, COMPRESS_OPTS);
      if (job !== jobRef.current) return;
      // Keep original filename stem
      const stem = (f.name || "photo").replace(/\.[^.]+$/, "");
      const ext = (compressed.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
      const out = new File([compressed], `${stem}.${ext}`, { type: compressed.type });
      // Use whichever is smaller — never upload a bigger re-encode
      onSelect(out.size < f.size ? out : f);
    } catch (err) {
      if (job !== jobRef.current) return;
      if (f.size <= BACKEND_LIMIT_BYTES) {
        toast("Couldn't optimize the photo — uploading the original instead.");
        onSelect(f);
      } else {
        toast.error("This photo is too large to upload (max 10MB). Please choose a smaller one.");
      }
    } finally {
      if (job === jobRef.current) setBusy(false);
    }
  };

  const clear = () => {
    jobRef.current++;
    setBusy(false);
    onSelect(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div data-testid={testIdPrefix}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
        data-testid={`${testIdPrefix}-input`}
      />

      {compressing && !preview && (
        <div
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm tracking-wide bg-accent-motif text-motif"
          data-testid={`${testIdPrefix}-preparing`}
          aria-live="polite"
        >
          <Loader2 className="w-4 h-4 animate-spin text-primary-motif" />
          Preparing photo…
        </div>
      )}

      {preview ? (
        <div className="flex items-center gap-3" data-testid={`${testIdPrefix}-preview`}>
          <img
            src={preview}
            alt={file?.name ? `Selected photo: ${file.name}` : "Selected photo"}
            className="h-20 w-20 object-cover rounded-xl shadow-sm flex-shrink-0"
          />
          <div className="flex flex-col gap-1.5 min-w-0">
            {file?.name && (
              <div className="text-[10px] text-motif-muted truncate max-w-[180px]" data-testid={`${testIdPrefix}-filename`}>
                {file.name}
                {file?.size ? <span className="ml-1 opacity-70" data-testid={`${testIdPrefix}-size`}>· {fmtSize(file.size)}</span> : null}
              </div>
            )}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={compressing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs tracking-wide bg-accent-motif text-motif transition hover:opacity-90 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              aria-label="Replace photo"
              data-testid={`${testIdPrefix}-replace`}
            >
              <RefreshCw className="w-3 h-3 text-primary-motif" strokeWidth={1.5} />
              {T("Replace Photo")}
            </button>
            <button
              type="button"
              onClick={clear}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs tracking-wide bg-accent-motif text-motif transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              aria-label="Remove photo"
              data-testid={`${testIdPrefix}-remove`}
            >
              <Trash2 className="w-3 h-3 text-primary-motif" strokeWidth={1.5} />
              {T("Remove Photo")}
            </button>
          </div>
        </div>
      ) : (
        !compressing && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm tracking-wide bg-accent-motif text-motif transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            aria-label="Upload a photo"
            data-testid={`${testIdPrefix}-upload`}
          >
            <ImagePlus className="w-4 h-4 text-primary-motif" strokeWidth={1.5} />
            {T("Upload Photo")}
          </button>
        )
      )}
    </div>
  );
}
